import { useEffect } from 'react';
import { Portal } from '@/components/shared/Portal';
import type { EventResultAsset } from '@/types/domain';

interface ResultImageLightboxProps {
  asset: EventResultAsset;
  currentIndex: number;
  totalCount: number;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

export function ResultImageLightbox({
  asset,
  currentIndex,
  totalCount,
  onClose,
  onNext,
  onPrevious,
}: ResultImageLightboxProps): JSX.Element {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }

      if (event.key === 'ArrowRight' && onNext) {
        onNext();
      }

      if (event.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrevious]);

  return (
    <Portal>
      <div className="result-lightbox" role="dialog" aria-modal="true" aria-labelledby="result-lightbox-title">
        <div className="result-lightbox__backdrop" onClick={onClose} />
        <div className="result-lightbox__panel">
          <div className="result-lightbox__header">
            <div>
              <p className="guild-chat__eyebrow">RESULT VIEWER</p>
              <strong id="result-lightbox-title">
                Image {currentIndex + 1} of {totalCount}
              </strong>
              {asset.caption ? <p className="copy-block">{asset.caption}</p> : null}
            </div>
            <div className="confirmation-dialog__actions">
              {onPrevious ? (
                <button type="button" className="button button--ghost" onClick={onPrevious}>
                  Previous
                </button>
              ) : null}
              {onNext ? (
                <button type="button" className="button button--ghost" onClick={onNext}>
                  Next
                </button>
              ) : null}
              <button type="button" className="button button--secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>

          <div className="result-lightbox__media">
            <img src={asset.imageUrl} alt={asset.caption ?? 'Mass result image'} />
          </div>
        </div>
      </div>
    </Portal>
  );
}
